/**
 * ===================================================
 * 🤖 DETECTOR DE BOTS - FILTRE PER GA4
 * ===================================================
 *
 * Detecta i filtra trànsit de bots (Cookiebot, etc.) abans de GA4.
 * Inclou script inline per al <head> (Html.jsx) i init al bundle (index.js).
 *
 * Patrons basats en resolució de pantalla (+ país quan cal via geo IP en cache).
 */

const COUNTRY_CACHE_KEY = '__ga4BotFilterCountry';

const OS_SIGNATURES = [
  'Windows',
  'Mac OS',
  'Macintosh',
  'Linux',
  'Android',
  'iPhone',
  'iPad',
  'CrOS',
];

/** GA4 mostra "(not set)" quan no pot inferir el SO del user-agent. */
export const hasUnsetOs = (ua) => {
  const normalized = (ua || '').trim();
  if (normalized.length < 10) return true;
  return !OS_SIGNATURES.some((sig) => normalized.indexOf(sig) > -1);
};

export const hasUnsetResolution = () => {
  if (typeof window === 'undefined') return false;
  const width = window.screen?.width;
  const height = window.screen?.height;
  return !(width > 0 && height > 0);
};

let cachedCountryCode = null;

export const getCountryCode = () => {
  if (cachedCountryCode) return cachedCountryCode;
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(COUNTRY_CACHE_KEY);
    if (stored) {
      cachedCountryCode = stored;
      return stored;
    }
  } catch (e) {
    // sessionStorage no disponible
  }
  return null;
};

const startCountryLookup = () => {
  if (typeof window === 'undefined' || getCountryCode()) return;

  fetch('https://ipwho.is/')
    .then((response) => response.json())
    .then((data) => {
      const code = data?.country_code;
      if (!code) return;
      cachedCountryCode = code;
      try {
        sessionStorage.setItem(COUNTRY_CACHE_KEY, code);
      } catch (e) {
        // sessionStorage no disponible
      }
    })
    .catch(() => {});
};

export const matchesBotPattern = (pattern, ua, width, height, countryCode) => {
  if (pattern.requireUnsetResolution) {
    if (width > 0 && height > 0) return false;
  } else if (width !== pattern.width || height !== pattern.height) {
    return false;
  }

  if (pattern.countries?.length) {
    if (!countryCode || !pattern.countries.includes(countryCode)) return false;
  }

  if (pattern.matchAnyOs) return true;
  if (pattern.requireUnsetOs) return hasUnsetOs(ua);
  return (pattern.os || []).some((osPattern) => ua.indexOf(osPattern) > -1);
};

export const BOT_PATTERNS = [
  {
    width: 1024,
    height: 768,
    os: ['Windows NT 10.0'],
    name: 'Cookiebot 1024x768 Win10',
  },
  {
    width: 1600,
    height: 1600,
    os: [
      'Windows NT 10.0',
      'Windows 10',
      'Windows 11',
      'Win64; x64',
      'WOW64',
    ],
    name: 'Bot 1600x1600 Windows',
  },
  {
    width: 1536,
    height: 864,
    os: ['Windows NT 10.0'],
    name: 'Bot 1536x864 Win10',
  },
  {
    width: 357,
    height: 667,
    os: ['iPhone', 'iOS 13.2.3', 'CPU iPhone OS 13_2_3', 'Mobile/15E148'],
    name: 'Bot 357x667 iOS13',
  },
  {
    width: 3840,
    height: 2160,
    os: ['Windows NT 10.0', 'Windows 10', 'Win64; x64', 'WOW64'],
    name: 'Bot 4K Win10',
  },
  {
    width: 1366,
    height: 1366,
    os: ['Intel Mac OS X 10_15', 'Intel Mac OS X 10.15'],
    name: 'Bot 1366x1366 macOS 10.15',
  },
  // Resolucions sospitoses globals (qualsevol país)
  {
    width: 1280,
    height: 1200,
    matchAnyOs: true,
    name: 'Bot 1280x1200',
  },
  {
    width: 800,
    height: 600,
    matchAnyOs: true,
    name: 'Bot 800x600',
  },
  {
    width: 2048,
    height: 1152,
    matchAnyOs: true,
    name: 'Bot 2048x1152',
  },
  {
    width: 1280,
    height: 720,
    requireUnsetOs: true,
    name: 'Bot 1280x720 NoOS',
  },
  {
    width: 1366,
    height: 768,
    requireUnsetOs: true,
    name: 'Bot 1366x768 NoOS',
  },
  {
    width: 1920,
    height: 1080,
    requireUnsetOs: true,
    name: 'Bot 1920x1080 NoOS',
  },
  {
    width: 1280,
    height: 1280,
    requireUnsetOs: true,
    name: 'Bot 1280x1280 NoOS',
  },
  // Resolució + país (informe GA4 resolució × país)
  {
    width: 1920,
    height: 1080,
    countries: ['SG', 'CN', 'CO'],
    matchAnyOs: true,
    name: 'Bot 1920x1080 SG/CN/CO',
  },
  // Bots mòbils Singapur (~100% implicació, ~1 min)
  {
    width: 720,
    height: 1280,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 720x1280 Singapore',
  },
  {
    width: 1080,
    height: 1920,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 1080x1920 Singapore',
  },
  {
    width: 480,
    height: 854,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 480x854 Singapore',
  },
  {
    width: 480,
    height: 800,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 480x800 Singapore',
  },
  {
    width: 1080,
    height: 2340,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 1080x2340 Singapore',
  },
  {
    width: 720,
    height: 1520,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 720x1520 Singapore',
  },
  {
    width: 540,
    height: 960,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 540x960 Singapore',
  },
  {
    width: 1440,
    height: 2960,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 1440x2960 Singapore',
  },
  {
    width: 720,
    height: 1440,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 720x1440 Singapore',
  },
  {
    width: 1080,
    height: 2280,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot 1080x2280 Singapore',
  },
  {
    requireUnsetResolution: true,
    countries: ['SG'],
    matchAnyOs: true,
    name: 'Bot Singapore unset resolution',
  },
];

export const isBotTraffic = () => {
  if (typeof window === 'undefined') return false;

  try {
    const width = window.screen.width;
    const height = window.screen.height;
    const ua = navigator.userAgent || '';
    const countryCode = getCountryCode();

    return BOT_PATTERNS.some((pattern) =>
      matchesBotPattern(pattern, ua, width, height, countryCode),
    );
  } catch (e) {
    return false;
  }
};

const wrapDataLayerPush = () => {
  window.dataLayer = window.dataLayer || [];
  if (window.dataLayer.__ga4BotFilterPush) return;

  const originalPush = window.dataLayer.push.bind(window.dataLayer);
  window.dataLayer.push = function ga4BotFilterPush() {
    if (isBotTraffic()) {
      return window.dataLayer.length;
    }
    return originalPush.apply(window.dataLayer, arguments);
  };
  window.dataLayer.__ga4BotFilterPush = true;
};

const wrapGtag = () => {
  let underlying = window.gtag;
  if (underlying && underlying.__ga4BotFilterWrapped) {
    underlying = underlying.__ga4BotFilterUnderlying;
  }

  const wrapped = function ga4BotFilterGtag() {
    if (isBotTraffic()) return;
    if (typeof underlying === 'function') {
      return underlying.apply(this, arguments);
    }
    return window.dataLayer.push(arguments);
  };
  wrapped.__ga4BotFilterWrapped = true;
  wrapped.__ga4BotFilterUnderlying = underlying;
  window.gtag = wrapped;
};

/** Instal·la o re-instal·la el filtre (p.ex. després que carregui gtag/js). */
export const initGA4BotFilter = () => {
  if (typeof window === 'undefined') return;

  startCountryLookup();
  wrapDataLayerPush();
  wrapGtag();
  window.__isBotTraffic = isBotTraffic;
  window.__initGA4BotFilter = initGA4BotFilter;
};

const sanitizeGa4Code = (ga4Code) =>
  String(ga4Code || '').replace(/[^a-zA-Z0-9-]/g, '');

/** Script síncron al <head>: s'executa abans de gtag('config'). */
export const getGA4BotFilterInlineScript = () => `
(function() {
  var COUNTRY_CACHE_KEY = ${JSON.stringify(COUNTRY_CACHE_KEY)};
  var OS_SIGNATURES = ${JSON.stringify(OS_SIGNATURES)};
  var BOT_PATTERNS = ${JSON.stringify(BOT_PATTERNS)};
  var cachedCountryCode = null;

  function hasUnsetOs(ua) {
    var normalized = (ua || '').trim();
    if (normalized.length < 10) return true;
    for (var i = 0; i < OS_SIGNATURES.length; i++) {
      if (normalized.indexOf(OS_SIGNATURES[i]) > -1) return false;
    }
    return true;
  }

  function getCountryCode() {
    if (cachedCountryCode) return cachedCountryCode;
    try {
      var stored = sessionStorage.getItem(COUNTRY_CACHE_KEY);
      if (stored) {
        cachedCountryCode = stored;
        return stored;
      }
    } catch (e) {}
    return null;
  }

  function startCountryLookup() {
    if (getCountryCode()) return;
    fetch('https://ipwho.is/')
      .then(function(response) { return response.json(); })
      .then(function(data) {
        var code = data && data.country_code;
        if (!code) return;
        cachedCountryCode = code;
        try {
          sessionStorage.setItem(COUNTRY_CACHE_KEY, code);
        } catch (e) {}
      })
      .catch(function() {});
  }

  function matchesBotPattern(pattern, ua, width, height, countryCode) {
    if (pattern.requireUnsetResolution) {
      if (width > 0 && height > 0) return false;
    } else if (width !== pattern.width || height !== pattern.height) {
      return false;
    }

    if (pattern.countries && pattern.countries.length) {
      if (!countryCode || pattern.countries.indexOf(countryCode) === -1) {
        return false;
      }
    }

    if (pattern.matchAnyOs) return true;
    if (pattern.requireUnsetOs) return hasUnsetOs(ua);

    var osList = pattern.os || [];
    for (var o = 0; o < osList.length; o++) {
      if (ua.indexOf(osList[o]) > -1) return true;
    }
    return false;
  }

  function isBotTraffic() {
    try {
      var width = window.screen.width;
      var height = window.screen.height;
      var ua = navigator.userAgent || '';
      var countryCode = getCountryCode();
      for (var p = 0; p < BOT_PATTERNS.length; p++) {
        if (
          matchesBotPattern(
            BOT_PATTERNS[p],
            ua,
            width,
            height,
            countryCode,
          )
        ) {
          return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  function wrapDataLayerPush() {
    window.dataLayer = window.dataLayer || [];
    if (window.dataLayer.__ga4BotFilterPush) return;
    var originalPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = function() {
      if (isBotTraffic()) return window.dataLayer.length;
      return originalPush.apply(window.dataLayer, arguments);
    };
    window.dataLayer.__ga4BotFilterPush = true;
  }

  function wrapGtag() {
    var underlying = window.gtag;
    if (underlying && underlying.__ga4BotFilterWrapped) {
      underlying = underlying.__ga4BotFilterUnderlying;
    }
    var wrapped = function() {
      if (isBotTraffic()) return;
      if (typeof underlying === 'function') {
        return underlying.apply(this, arguments);
      }
      return window.dataLayer.push(arguments);
    };
    wrapped.__ga4BotFilterWrapped = true;
    wrapped.__ga4BotFilterUnderlying = underlying;
    window.gtag = wrapped;
  }

  function initGA4BotFilter() {
    startCountryLookup();
    wrapDataLayerPush();
    wrapGtag();
    window.__isBotTraffic = isBotTraffic;
    window.__initGA4BotFilter = initGA4BotFilter;
  }

  initGA4BotFilter();
})();
`.trim();

export const getGA4ConfigInlineScript = (ga4Code) => {
  const safeCode = sanitizeGa4Code(ga4Code);
  if (!safeCode) return '';
  return `
gtag('js', new Date());
gtag('config', '${safeCode}');
(function() {
  function reapplyGA4BotFilter() {
    if (window.__initGA4BotFilter) window.__initGA4BotFilter();
  }
  var gtagScript = document.querySelector(
    'script[src*="googletagmanager.com/gtag/js"]',
  );
  if (gtagScript) {
    if (gtagScript.complete || gtagScript.readyState === 'complete') {
      reapplyGA4BotFilter();
    } else {
      gtagScript.addEventListener('load', reapplyGA4BotFilter);
    }
  }
})();
`.trim();
};

export default isBotTraffic;
